import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, Chip, Tabs, Tab, Badge, Avatar, CardActionArea, Drawer, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { FaChartBar, FaClock, FaFire } from 'react-icons/fa';
import { useFavorites } from '../../context/FavoritesContext';
import { usePermissions } from '../../context/PermissionContext';
import { TOOL_COLORS } from '../../constants/colors';

const RECENT_VISITS_KEY = 'recentPageVisits';
const REPORTS_COLOR = TOOL_COLORS.REPORTS.primary;

const ReportsLanding = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { favorites, toggleFavorite, isFavorite } = useFavorites();
  const { isAdmin, isOwner, hasAccessToPath } = usePermissions();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedTool, setSelectedTool] = useState(null);
  const [lastUsedMap, setLastUsedMap] = useState({});

  useEffect(() => {
    const loadLastUsedTimes = () => {
      try {
        const stored = localStorage.getItem(RECENT_VISITS_KEY);
        if (stored) {
          const visits = JSON.parse(stored);
          const timeMap = {};
          visits.forEach(visit => {
            if (!timeMap[visit.path]) {
              timeMap[visit.path] = new Date(visit.timestamp);
            }
          });
          setLastUsedMap(timeMap);
        }
      } catch (err) {
        console.error('Error loading last used times:', err);
      }
    };
    loadLastUsedTimes();
  }, []);

  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  };

  const getLastUsed = (path) => {
    return formatTimeAgo(lastUsedMap[path]);
  };

  const reportsInterfaces = [
    {
      name: 'Sales Dashboard',
      path: '/dashboard/sales-dashboard',
      description: 'Interactive sales analytics dashboard with top SKUs, customers, collections, sizes, colors, styles, warehouses, and zip code breakdowns. Visualize revenue and sales performance across multiple dimensions.',
      icon: FaChartBar,
      category: 'Analytics',
      isNew: false,
    },
    {
      name: 'Sales Dashboard V2',
      path: '/dashboard/sales-dashboard-v2',
      description: 'Fast sales analytics on the self-hosted ClickHouse warehouse: revenue with a 3-month forecast, top SKUs and customers, collections, warehouses, states, drop-ship split and product images. Filter by date, source, warehouse, state, customer and collection.',
      icon: FaChartBar,
      category: 'Analytics',
      isNew: true,
    },
    {
      name: 'Sales Pulse',
      path: '/dashboard/sales-pulse',
      description: 'The sales team daily action list: every account scored growing / steady / fading / at-risk / lost with window-over-window revenue, at-risk and momentum call lists, plus top gaining and declining items, collections and customers.',
      icon: FaChartBar,
      category: 'Analytics',
      isNew: true,
    },
    {
      name: 'Collection Launches',
      path: '/dashboard/collection-launches',
      description: 'Is a new collection tracking like a winner? Every launch since 2022 gets a cumulative-revenue ramp compared to the whole cohort at the same age (P25-P75 band), with statuses from Outperforming to Stalled and a no-sales-yet watchlist.',
      icon: FaChartBar,
      category: 'Analytics',
      isNew: true,
    },
    {
      name: 'Customer Style Gaps',
      path: '/dashboard/customer-style-gaps',
      description: 'What each customer has NOT built yet: pick a customer to browse every eligible catalog item missing from their assortment (with stock, prices and thumbnails), or search any SKU to see which of the ~104 CSL customers carry it. Interactive version of the Customer Styles Excel tool.',
      icon: FaChartBar,
      category: 'Analytics',
      isNew: true,
    },
    {
      name: 'New Items',
      path: '/dashboard/new-items',
      description: 'The selling team\'s launchpad: every recently created product with listing readiness (UPC, photo, copy, dims), live and incoming stock with PO ETAs, full pricing, and whether anyone has started selling it — plus a copy-paste listing kit per item.',
      icon: FaChartBar,
      category: 'Analytics',
      isNew: true,
    },
    {
      name: 'Returns & Quality',
      path: '/dashboard/returns-dashboard',
      description: 'Return credits vs sales on the ClickHouse warehouse: credited dollars, unit and dollar return rates, net revenue, OSR vs credit-return split, and top offending SKUs, collections and customers with per-row return rates.',
      icon: FaChartBar,
      category: 'Analytics',
      isNew: false,
    },
    {
      name: 'RetailOS',
      path: '/dashboard/retail-os',
      description: 'Retail operations command center for the Walmart, Home Depot and Lowe’s accounts: executive overview, account and SKU intelligence, advertising ROAS, four-channel promotion calendar and SKU-level promotion-lift analysis. Account sales are live from the warehouse.',
      icon: FaChartBar,
      category: 'Analytics',
      isNew: true,
    },
    {
      name: 'Reorder Console',
      path: '/dashboard/reorder-console',
      description: 'Daily reorder engine: ORDER / EXPEDITE / MONITOR queues, size planner, open POs and buyer decisions. Reference only, never auto-orders.',
      icon: FaChartBar,
      category: 'Analytics',
      isNew: true,
    },
  ];

  const categories = ['All', 'Analytics'];

  const permittedInterfaces = useMemo(() => {
    if (isAdmin || isOwner) {
      return reportsInterfaces;
    }
    return reportsInterfaces.filter(item => {
      const path = item.path.startsWith('/dashboard/')
        ? item.path.replace('/dashboard/', '')
        : item.path;
      return hasAccessToPath(path);
    });
  }, [isAdmin, isOwner, hasAccessToPath]);

  const filteredTools = selectedTab === 0
    ? permittedInterfaces
    : permittedInterfaces.filter(tool => tool.category === categories[selectedTab]);

  const handleToolClick = (item) => {
    setSelectedTool(item.path);
    navigate(item.path);
  };

  const sidebarContent = () => (
    <Paper
      elevation={0}
      sx={{
        width: 280,
        height: '100%',
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'white',
        borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: REPORTS_COLOR }}>
            <FaChartBar size={28} color="white" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Analytics
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {permittedInterfaces.length} tool{permittedInterfaces.length !== 1 ? 's' : ''} available
            </Typography>
          </Box>
        </Box>
      </Box>

      <Tabs
        orientation="vertical"
        value={selectedTab}
        onChange={(e, newValue) => { setSelectedTab(newValue); if (isMobile) setSidebarOpen(false); }}
        sx={{
          borderRight: 0,
          '& .MuiTabs-indicator': { left: 0, width: 4, bgcolor: REPORTS_COLOR },
        }}
      >
        {categories.map((category, index) => (
          <Tab
            key={category}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                <Typography variant="body2">{category}</Typography>
                <Chip
                  label={index === 0
                    ? permittedInterfaces.length
                    : permittedInterfaces.filter(t => t.category === category).length
                  }
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            }
            sx={{
              textAlign: 'left',
              alignItems: 'flex-start',
              px: 3,
              py: 1.5,
              minHeight: 48,
              textTransform: 'none',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
              },
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(47, 196, 178, 0.08)' : 'rgba(47, 196, 178, 0.04)',
                transform: 'translateX(4px)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                width: '0%',
                height: '3px',
                borderRadius: '2px',
                transform: 'translateX(-50%)',
                transition: 'width 0.3s ease-in-out',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 10' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q25 0 50 5 T100 5' fill='none' stroke='%232FC4B2' stroke-width='4'/%3E%3C/svg%3E")`,
                backgroundSize: '100% 100%',
              },
              '&:hover::after': {
                width: '80%',
              },
            }}
          />
        ))}
      </Tabs>

      <Box sx={{ mt: 'auto', p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Quick Stats</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Recently Used</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {permittedInterfaces.filter(t => lastUsedMap[t.path]).length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Favorites</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#FFD700' }}>
              {favorites.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">New Tools</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#FF6B6B' }}>
              {permittedInterfaces.filter(t => t.isNew).length}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: theme.palette.background.default }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)} PaperProps={{ sx: { width: 280 } }}>
          {sidebarContent()}
        </Drawer>
      ) : (
        sidebarContent()
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box
          sx={{
            p: 3,
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'white',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton onClick={() => setSidebarOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {categories[selectedTab] === 'All' ? 'All' : categories[selectedTab]} Tools
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                Live analytics dashboards for sales, returns, revenue and business insights
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(350px, 1fr))' }, gap: 2 }}>
            {filteredTools.map((item) => {
              const IconComponent = item.icon || FaChartBar;
              const isFav = isFavorite(item.path);
              const isSelected = selectedTool === item.path;

              return (
                <Paper
                  key={item.path}
                  elevation={0}
                  sx={{
                    border: `2px solid ${isSelected ? REPORTS_COLOR : theme.palette.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: isSelected
                      ? theme.palette.mode === 'dark' ? `${REPORTS_COLOR}10` : `${REPORTS_COLOR}05`
                      : 'transparent',
                    '&:hover': {
                      borderColor: REPORTS_COLOR,
                      transform: 'translateX(4px)',
                      boxShadow: theme.shadows[2],
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: '0%',
                      height: '4px',
                      background: REPORTS_COLOR,
                      borderRadius: '2px',
                      transform: 'translateX(-50%)',
                      transition: 'width 0.4s ease-in-out',
                    },
                    '&:hover::after': { width: '90%' },
                  }}
                >
                  <CardActionArea onClick={() => handleToolClick(item)} sx={{ p: 2.5, height: '100%' }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1,
                          bgcolor: `${REPORTS_COLOR}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <IconComponent size={24} color={REPORTS_COLOR} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{
                              fontWeight: 600, flex: 1, minWidth: 0,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>
                              {item.name}
                            </Typography>
                            {item.isNew && (
                              <Chip label="NEW" size="small" sx={{
                                bgcolor: 'error.main', color: 'white', height: 20,
                                fontSize: '0.6rem', fontWeight: 700, ml: 1, flexShrink: 0
                              }} />
                            )}
                          </Box>
                          <Tooltip title={isFav ? 'Remove from favorites' : 'Add to favorites'}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite({ text: item.name, path: item.path, category: 'reports' });
                              }}
                              sx={{ ml: 1, color: isFav ? '#FFD700' : theme.palette.action.disabled }}
                            >
                              {isFav ? '\u2605' : '\u2606'}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography variant="body2" sx={{
                          color: theme.palette.text.secondary, mb: 1,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {item.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FaClock size={12} color={theme.palette.text.secondary} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {getLastUsed(item.path)}
                          </Typography>
                          <Box sx={{ flex: 1 }} />
                          <Chip label={item.category} size="small" sx={{
                            height: 20, fontSize: '0.7rem', bgcolor: `${REPORTS_COLOR}10`, color: theme.palette.text.primary,
                          }} />
                        </Box>
                      </Box>
                    </Box>
                  </CardActionArea>
                </Paper>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ReportsLanding;
